import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography,
    IconButton, Autocomplete
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CasinoIcon from '@mui/icons-material/Casino'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiGetUserSpinHistory } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'

interface SpinEntry {
    _id: string
    type?: string
    direction?: 'CREDIT' | 'DEBIT'
    spinCount?: number
    earnedAmount: number
    referenceType?: string | null
    referenceId?: string | null
    seen?: boolean
    createdAt: string
}

interface SummaryRow {
    _id: { type: string; direction: 'CREDIT' | 'DEBIT' }
    totalSpins: number
    count: number
}

interface Pagination {
    total: number
    page: number
    limit: number
    totalPages: number
}

const SPIN_LOG_TYPES = [
    'SPIN_REDEEMED', 'REFERRAL_BONUS', 'DEPOSIT_BONUS',
    'SIGNUP_BONUS', 'COMEBACK_BONUS', 'ADMIN_GRANT', 'ADMIN_REVOKE',
]

const TYPE_LABELS: Record<string, string> = {
    SPIN_REDEEMED: 'Spin Redeemed',
    REFERRAL_BONUS: 'Referral Bonus',
    DEPOSIT_BONUS: 'Deposit Bonus',
    SIGNUP_BONUS: 'Signup Bonus',
    COMEBACK_BONUS: 'Comeback Bonus',
    ADMIN_GRANT: 'Admin Grant',
    ADMIN_REVOKE: 'Admin Revoke',
}

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const DirectionPill: React.FC<{ direction?: 'CREDIT' | 'DEBIT' }> = ({ direction }) => {
    if (!direction) return <Typography fontSize={11} color="#3a3a3a">—</Typography>
    const isCredit = direction === 'CREDIT'
    const color = isCredit ? '#10b981' : '#ef4444'
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: 11, fontWeight: 700,
            color, backgroundColor: color + '18', border: `1px solid ${color}50`,
        }}>
            {isCredit ? <TrendingUpIcon sx={{ fontSize: 13 }} /> : <TrendingDownIcon sx={{ fontSize: 13 }} />}
            {direction}
        </Box>
    )
}

const TypePill: React.FC<{ type?: string }> = ({ type }) => {
    if (!type) return <Typography fontSize={11} color="#3a3a3a">—</Typography>
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.4, borderRadius: '6px',
            fontSize: 11, fontWeight: 600, color: '#a78bfa', backgroundColor: '#a78bfa15',
            border: '1px solid #a78bfa40', whiteSpace: 'nowrap',
        }}>
            {TYPE_LABELS[type] || type}
        </Box>
    )
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333', borderRadius: '14px', p: 2.5,
        position: 'relative', overflow: 'hidden',
        '&::before': {
            content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const UserSpinHistory: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { userId, userName } = (location.state || {}) as { userId?: string; userName?: string }

    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('spin-history', 'all')

    const [entries, setEntries] = useState<SpinEntry[]>([])
    const [summary, setSummary] = useState<SummaryRow[]>([])
    const [userMeta, setUserMeta] = useState<{ name: string; email: string; currentBalance: number } | null>(null)
    const [pagination, setPagination] = useState<Pagination>({ total: 0, page: 1, limit: 10, totalPages: 1 })

    const [page, setPage] = usePersistedState('spinHistory:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('spinHistory:rowsPerPage', 10)
    const [type, setType] = usePersistedState('spinHistory:type', '')
    const [direction, setDirection] = usePersistedState('spinHistory:direction', '')

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

    const fetchHistory = useCallback(async () => {
        if (!userId) return
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (type) params.append('type', type)
            if (direction) params.append('direction', direction)
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }

            const res = await ApiGetUserSpinHistory(userId, `?${params.toString()}`)
            if (res?.status) {
                setEntries(res.data?.entries || [])
                setSummary(res.data?.summary || [])
                setUserMeta(res.data?.user || null)
                setPagination(res.meta?.pagination || res.data?.pagination || {})
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || 'Failed to fetch spin history',
            })
        }
    }, [userId, page, rowsPerPage, type, direction, buildQuery])

    useEffect(() => { fetchHistory() }, [fetchHistory])

    const prevFiltersRef = React.useRef({ type, direction, range, customStart, customEnd })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.type !== type || prev.direction !== direction || prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf())
        if (changed) setPage(0)
        prevFiltersRef.current = { type, direction, range, customStart, customEnd }
    }, [type, direction, range, customStart, customEnd, setPage])

    const totalCredited = summary.filter(s => s._id.direction === 'CREDIT').reduce((a, s) => a + s.totalSpins, 0)
    const totalDebited = summary.filter(s => s._id.direction === 'DEBIT').reduce((a, s) => a + s.totalSpins, 0)

    if (!userId) {
        return (
            <Box className="common-layout" sx={{ p: 4 }}>
                <Typography color="#7a7a7a">No user selected. Go back and pick a user first.</Typography>
            </Box>
        )
    }

    return (
        <div className="common-layout flex-column">
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-2 align-items-center">
                    <IconButton onClick={() => navigate(-1)} size="small">
                        <ArrowBackIcon sx={{ color: '#fff' }} />
                    </IconButton>
                    <Box>
                        <Typography className="fs-24 text-white fw-medium" sx={{ lineHeight: 1.1 }}>
                            Spin History
                        </Typography>
                        <Typography fontSize={12} color="#c0c0c0">
                            {userMeta?.name || userName || '—'} {userMeta?.email ? `· ${userMeta.email}` : ''}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <StatCard label="Current Spins" value={String(userMeta?.currentBalance ?? 0)}
                    sub="available spins" color="#3b82f6"
                    icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} />
                <StatCard label="Total Credited Spins" value={`+${totalCredited}`}
                    sub="spins earned" color="#10b981"
                    icon={<TrendingUpIcon fontSize="small" />} />
                <StatCard label="Total Debited Spins" value={`-${totalDebited}`}
                    sub="spins used" color="#ef4444"
                    icon={<TrendingDownIcon fontSize="small" />} />
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography fontSize={13} fontWeight={700} color="#e0e0e0">
                        Spin Log
                        {pagination.total > 0 && (
                            <Box component="span" sx={{ ml: 1, background: 'rgba(255,255,255,0.08)', borderRadius: '10px', px: 1, py: 0.2, fontSize: 11 }}>
                                {pagination.total}
                            </Box>
                        )}
                    </Typography>

                    <Box className="d-flex align-items-center gap-2" sx={{ flexWrap: 'wrap' }}>
                        <Autocomplete
                            size="small"
                            value={type}
                            disableClearable={!type}
                            onChange={(_, v) => setType(v || '')}
                            options={SPIN_LOG_TYPES}
                            getOptionLabel={(o) => TYPE_LABELS[o] || o}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Type" sx={typeFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />
                        <Autocomplete
                            size="small"
                            value={direction}
                            disableClearable={!direction}
                            onChange={(_, v) => setDirection(v || '')}
                            options={['CREDIT', 'DEBIT']}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Direction" sx={directionFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'Type', 'Direction', 'Spins', 'Amount', 'Reference', 'Date & Time', 'Seen'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.length > 0 ? entries.map((e, i) => (
                                <TableRow key={e._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                    <TableCell sx={tdStyle}><TypePill type={e.type} /></TableCell>
                                    <TableCell sx={tdStyle}><DirectionPill direction={e.direction} /></TableCell>
                                    <TableCell sx={tdStyle}>
                                        {e.spinCount != null ? (
                                            <Typography fontSize={13} fontWeight={700} color={e.direction === 'CREDIT' ? '#10b981' : '#ef4444'}>
                                                {e.direction === 'CREDIT' ? '+' : '-'}{e.spinCount}
                                            </Typography>
                                        ) : <Typography fontSize={11} color="#3a3a3a">—</Typography>}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {e.earnedAmount ? <Typography fontSize={13} color="#9a9a9a">${e.earnedAmount.toFixed(2)}</Typography> : <Typography fontSize={11} color="#3a3a3a">-</Typography>}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {e.referenceId ? (
                                            <Tooltip title={`${e.referenceType || ''} ${e.referenceId}`} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {e.referenceId}
                                                </Typography>
                                            </Tooltip>
                                        ) : <Typography fontSize={11} color="#3a3a3a">-</Typography>}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{formatDate(e.createdAt)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{formatTime(e.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {e.seen
                                            ? <Typography fontSize={11} color="#10b981">Seen</Typography>
                                            : <Typography fontSize={11} color="#6f6f6f">Unseen</Typography>}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <CasinoIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No spin activity found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default UserSpinHistory

const thStyle = {
    backgroundColor: '#241815', color: '#ff6a3d', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const,
    py: 1.5, borderBottom: '1px solid #2a2a2a !important', position: 'sticky' as const, top: 0, zIndex: 2,
}

const tdStyle = { color: '#c0c0c0', py: 1.4, fontSize: 13, whiteSpace: 'nowrap' as const }

const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ff6a3d08' } }

const searchFieldStyle = {
    width: 200, background: 'transparent', borderRadius: '7px', border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const typeFieldStyle = { ...searchFieldStyle, width: 240 }
const directionFieldStyle = { ...searchFieldStyle, width: 160 }

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}